import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { DeleteUserFavoriteMutation, UserFavoriteDeleteInput } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EVENT_USER_FAVORITE_DELETED, trackUserFavoriteEvent } from "./tracking";
import { deleteUserFavoriteMutationDocument } from "./user-favorite.gql";
export type DeletedUserFavorite = DeleteUserFavoriteMutation["deleteUserFavorite"];

export interface UseDeleteUserFavoriteProps {
  onCompleted?: (deletedUserFavorite: DeletedUserFavorite) => void;
  onError?: (error: Error) => void;
}

export const useDeleteUserFavorite = ({ onCompleted, onError }: UseDeleteUserFavoriteProps = {}) => {
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const queryClient = useQueryClient();

  const {
    mutate: deleteFavorite,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: async (input: UserFavoriteDeleteInput): Promise<DeleteUserFavoriteMutation> => {
      const client = createOperationClient("deleteUserFavorite");
      return await client.request(deleteUserFavoriteMutationDocument, { input });
    },
    onSuccess: (data) => {
      const userFavorite = data?.deleteUserFavorite;
      if (userFavorite) {
        trackUserFavoriteEvent(EVENT_USER_FAVORITE_DELETED, {
          userFavoriteId: userFavorite.id,
        });
      }

      // Invalidate user favorites query
      if (currentWorkspace?.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.details(), // We'll need to add user favorites query key
        });
      }

      if (onCompleted) {
        onCompleted(data.deleteUserFavorite);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteFavorite,
    loading,
    error,
  };
};
